import { ReactNode, useState } from "react";
import { styled } from "styled-components";
import { Checkbox, Form, UploadFile } from "antd";
import {
  MdCollections,
  MdImage,
  MdInfoOutline,
  MdNotes,
  MdPhotoLibrary,
  MdVisibility,
} from "react-icons/md";
import { ImageUploader } from "../../../../components/ui/image-uploader/image-uploader.component";
import { StatePair } from "../../../../types/SetState";
import { Poster } from "../../../../types/Poster";
import { TextAreaResize } from "../../../../components/ui/textarea-resize/textarea-resize.component";
import { colors } from "../../../../theme/colors";
import { Section, card } from "../../components/section-card.component";

export const StepSix = ({
  verticalPoster,
  setVerticalPoster,
  horizontalPoster,
  setHorizontalPoster,
  productionStills,
  setProductionStills,
  directorsStatement,
  setDirectorsStatement,
  producersStatement,
  setProducersStatement,
  otherInformation,
  setOtherInformation,
  visible = true,
  posterErrors,
}: StatePair<"verticalPoster", "setVerticalPoster", Poster[]> &
  StatePair<"horizontalPoster", "setHorizontalPoster", Poster[]> &
  StatePair<"productionStills", "setProductionStills", Poster[]> &
  StatePair<"directorsStatement", "setDirectorsStatement", string> &
  StatePair<"producersStatement", "setProducersStatement", string> &
  StatePair<"otherInformation", "setOtherInformation", string> & {
    visible?: boolean;
    posterErrors: [boolean, boolean, boolean];
  }) => {
  const [textAreaHeights, setTextAreaHeights] = useState([54, 54, 54]);

  const updateVerticalAt = (type: 0 | 1 | 2) => ({
    url: verticalPoster.filter((p) => p.type === type).map((p) => p.url ?? ""),
    onRemove: (file: UploadFile) => {
      const next = [...verticalPoster];
      const idx = next.findIndex(
        (p) => p.url === file.url && p.type === type
      );
      next.splice(idx, 1);
      setVerticalPoster(next);
    },
    onComplete: (urls: UploadFile[]) => {
      setVerticalPoster([
        ...verticalPoster,
        {
          url: urls.at(-1)?.response[0],
          file: urls.at(-1)?.response[1],
          type,
        },
      ]);
    },
  });

  const updateHorizontalAt = (type: 0 | 1 | 2, aspectRatio: string) => ({
    url: horizontalPoster
      .filter((p) => p.type === type)
      .map((p) => p.url ?? ""),
    onRemove: (file: UploadFile) => {
      const next = [...horizontalPoster];
      const idx = next.findIndex(
        (p) => p.url === file.url && p.type === type
      );
      next.splice(idx, 1);
      setHorizontalPoster(next);
    },
    onComplete: (urls: UploadFile[]) => {
      setHorizontalPoster([
        ...horizontalPoster,
        {
          url: urls.at(-1)?.response[0],
          file: urls.at(-1)?.response[1],
          aspectRatio,
          type,
        },
      ]);
    },
  });

  return (
    <Wrapper style={{ display: visible ? "flex" : "none" }}>
      <Section
        icon={<MdImage />}
        title="Posters"
        subtitle="Upload poster artwork in different aspect ratios. JPG or PNG, up to 10MB each."
      >
        <SubHead>Vertical posters</SubHead>
        <PosterGrid>
          <UploadField
            label="Vertical Poster"
            aspect="3:4"
            help="At least one vertical poster is required."
            required
            error={
              posterErrors[0]
                ? "Please upload at least one vertical poster"
                : ""
            }
          >
            <ImageUploader
              maxCount={2}
              aspect={3 / 4}
              name="vertical_poster"
              action=""
              placeholder="Click or drag"
              hint="3:4 · max 2"
              outputAsJpeg
              {...updateVerticalAt(0)}
            />
          </UploadField>

          <UploadField label="Vertical Poster" aspect="4:5" help="Optional.">
            <ImageUploader
              maxCount={2}
              aspect={4 / 5}
              name="vertical_poster"
              action=""
              placeholder="Click or drag"
              hint="4:5 · max 2"
              outputAsJpeg
              {...updateVerticalAt(1)}
            />
          </UploadField>

          <UploadField label="Vertical Poster" aspect="1:1" help="Optional.">
            <ImageUploader
              maxCount={2}
              aspect={1 / 1}
              name="vertical_poster"
              action=""
              placeholder="Click or drag"
              hint="1:1 · max 2"
              outputAsJpeg
              {...updateVerticalAt(2)}
            />
          </UploadField>
        </PosterGrid>

        <Divider />

        <SubHead>Horizontal posters</SubHead>
        <PosterGrid>
          <UploadField
            label="Horizontal Poster"
            aspect="16:9"
            help="At least one horizontal poster is required."
            required
            error={
              posterErrors[1]
                ? "Please upload at least one horizontal poster"
                : ""
            }
          >
            <ImageUploader
              maxCount={2}
              aspect={16 / 9}
              name="horizontal_poster"
              action=""
              placeholder="Click or drag"
              hint="16:9 · max 2"
              outputAsJpeg
              {...updateHorizontalAt(0, "16_9")}
            />
          </UploadField>

          <UploadField label="Horizontal Poster" aspect="4:3" help="Optional.">
            <ImageUploader
              maxCount={2}
              aspect={4 / 3}
              name="horizontal_poster"
              action=""
              placeholder="Click or drag"
              hint="4:3 · max 2"
              outputAsJpeg
              {...updateHorizontalAt(1, "4_3")}
            />
          </UploadField>

          <UploadField
            label="Horizontal Poster"
            aspect="2.39:1"
            help="Optional."
          >
            <ImageUploader
              maxCount={2}
              aspect={2.39 / 1}
              name="horizontal_poster"
              action=""
              placeholder="Click or drag"
              hint="2.39:1 · max 2"
              outputAsJpeg
              {...updateHorizontalAt(2, "2_39_1")}
            />
          </UploadField>
        </PosterGrid>
      </Section>

      <Section
        icon={<MdCollections />}
        title="Production Stills"
        subtitle="Behind-the-scenes and on-set imagery. Square aspect, up to 5 images."
      >
        <UploadField label="Stills" aspect="1:1" help="Up to 5 images. Optional.">
          <ImageUploader
            maxCount={5}
            aspect={1 / 1}
            name="production_stills"
            action=""
            placeholder="Click or drag"
            hint="1:1 · max 5"
            outputAsJpeg
            url={productionStills.map((p) => p.url ?? "")}
            onRemove={(file) => {
              setProductionStills(
                productionStills.filter((el) => el.url !== file.url)
              );
            }}
            onComplete={(urls: UploadFile[]) => {
              setProductionStills([
                ...productionStills,
                {
                  url: urls.at(-1)?.response[0],
                  file: urls.at(-1)?.response[1],
                },
              ]);
            }}
          />
        </UploadField>
      </Section>

      <Section
        icon={<MdNotes />}
        title="Statements & Notes"
        subtitle="Share the story behind the film and any context you'd like distributors to know."
      >
        <CheckboxRow>
          <Form.Item
            name="is_screened"
            valuePropName="checked"
            style={{ marginBottom: 0 }}
          >
            <Checkbox>
              <MdVisibility
                style={{
                  verticalAlign: "middle",
                  marginRight: 6,
                  color: colors.primary.hex,
                }}
              />
              Has the film been screened for earlier viewing?
            </Checkbox>
          </Form.Item>
        </CheckboxRow>

        <StatementGrid>
          <StatementField label="Director's Statement" icon={<MdNotes />}>
            <Form.Item
              name="director_statement"
              style={{ marginBottom: 0, width: "100%" }}
            >
              <TextAreaResize
                placeholder="What was the director's vision?"
                onResized={(height) =>
                  setTextAreaHeights([
                    height,
                    textAreaHeights[1],
                    textAreaHeights[2],
                  ])
                }
                value={directorsStatement}
                onChange={setDirectorsStatement}
              />
            </Form.Item>
          </StatementField>

          <StatementField label="Producers' Statement" icon={<MdNotes />}>
            <Form.Item
              name="producer_statement"
              style={{ marginBottom: 0, width: "100%" }}
            >
              <TextAreaResize
                placeholder="What did the producers want to convey?"
                onResized={(height) =>
                  setTextAreaHeights([
                    textAreaHeights[0],
                    height,
                    textAreaHeights[2],
                  ])
                }
                value={producersStatement}
                onChange={setProducersStatement}
              />
            </Form.Item>
          </StatementField>
        </StatementGrid>

        <StatementField label="Other Information" icon={<MdInfoOutline />}>
          <Form.Item
            name="other_information"
            style={{ marginBottom: 0, width: "100%" }}
          >
            <TextAreaResize
              placeholder="Anything else worth noting…"
              onResized={(height) =>
                setTextAreaHeights([
                  textAreaHeights[0],
                  textAreaHeights[1],
                  height,
                ])
              }
              value={otherInformation}
              onChange={setOtherInformation}
            />
          </Form.Item>
        </StatementField>
      </Section>
    </Wrapper>
  );
};

const UploadField = ({
  label,
  aspect,
  help,
  required,
  error,
  children,
}: {
  label: string;
  aspect?: string;
  help?: string;
  required?: boolean;
  error?: string;
  children: ReactNode;
}) => (
  <UploadFieldShell>
    <FieldHead>
      <FieldLabel>
        <MdPhotoLibrary />
        <span>
          {label}
          {required ? <RequiredMark>*</RequiredMark> : null}
        </span>
      </FieldLabel>
      {aspect ? <AspectChip>{aspect}</AspectChip> : null}
    </FieldHead>
    <UploadBox>{children}</UploadBox>
    {help ? <Helper>{help}</Helper> : null}
    {error ? <ErrorText>{error}</ErrorText> : null}
  </UploadFieldShell>
);

const StatementField = ({
  label,
  icon,
  children,
}: {
  label: string;
  icon: ReactNode;
  children: ReactNode;
}) => (
  <UploadFieldShell>
    <FieldHead>
      <FieldLabel>
        {icon}
        <span>{label}</span>
      </FieldLabel>
    </FieldHead>
    <StatementBox>{children}</StatementBox>
  </UploadFieldShell>
);

const Wrapper = styled.div`
  width: 100%;
  flex-direction: column;
  gap: 1.6rem;

  &[style*="flex"] {
    display: flex !important;
  }
`;

const SubHead = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${colors.text.hex};
  opacity: 0.55;
`;

const Divider = styled.div`
  height: 1px;
  background: ${card.divider};
  width: 100%;
  margin: 0.4rem 0;
`;

const PosterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.4rem 1.6rem;
  width: 100%;
  container-type: inline-size;

  @container (max-width: 56rem) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @container (max-width: 32rem) {
    grid-template-columns: 1fr;
  }
`;

const UploadFieldShell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  min-width: 0;
  width: 100%;
`;

const FieldHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
`;

const FieldLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.3rem;
  font-weight: 500;
  color: ${colors.text.hex};
  line-height: 1.2;

  svg {
    flex: 0 0 auto;
    color: ${colors.primary.hex};
    font-size: 1.5rem;
    opacity: 0.85;
  }
`;

const RequiredMark = styled.span`
  color: ${colors.error.hex};
  margin-left: 0.2rem;
`;

const AspectChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.1rem 0.7rem;
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: ${colors.primary.hex};
  background: ${card.bg};
  border: 1px solid ${card.border};
  border-radius: 999px;
`;

const TILE_SIZE = "11rem";

const UploadBox = styled.div`
  display: block;
  width: 100%;

  & .ant-upload-wrapper,
  & .ant-upload-picture-card-wrapper {
    width: 100%;
  }

  & .ant-upload-list,
  & .ant-upload-list-picture-card {
    display: flex !important;
    flex-wrap: wrap;
    gap: 0.8rem;
    align-items: flex-start;
  }

  & .ant-upload-list-item-container,
  & .ant-upload-list-picture-card .ant-upload-list-item-container {
    width: ${TILE_SIZE} !important;
    height: ${TILE_SIZE} !important;
    margin: 0 !important;
    flex: 0 0 auto;
  }

  & .ant-upload-list-item,
  & .ant-upload-list-item-done {
    width: 100% !important;
    height: 100% !important;
    padding: 0 !important;
    border: 1px solid ${card.border} !important;
    border-radius: 0.7rem !important;
    overflow: hidden;
    background: ${card.innerBg} !important;
  }

  & .ant-upload-list-item::before {
    border-radius: 0.7rem;
  }

  & .ant-upload-list-item-thumbnail,
  & .ant-upload-list-item-image,
  & .ant-upload-list-item-info {
    width: 100% !important;
    height: 100% !important;
  }

  & .ant-upload-list-item-thumbnail img,
  & .ant-upload-list-item-image img {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
    border-radius: 0;
  }

  & .ant-upload-list-item-actions {
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
  }

  & .ant-upload-list-item-actions .anticon {
    color: #fff !important;
    font-size: 1.6rem;
  }

  & .ant-upload.ant-upload-select,
  & .ant-upload-select.ant-upload-select-picture-card {
    width: ${TILE_SIZE} !important;
    height: ${TILE_SIZE} !important;
    flex: 0 0 auto;
    background: ${card.innerBg} !important;
    border: 1.5px dashed ${card.border} !important;
    border-radius: 0.7rem !important;
    transition: border-color 0.15s, background 0.15s, color 0.15s;
    margin: 0 !important;
  }

  & .ant-upload.ant-upload-select:hover,
  & .ant-upload-select.ant-upload-select-picture-card:hover {
    border-color: ${colors.primary.hex} !important;
    background: ${card.accentSoft} !important;
  }

  & .ant-upload.ant-upload-select .ant-upload,
  & .ant-upload-select.ant-upload-select-picture-card .ant-upload {
    padding: 0.6rem !important;
    color: ${colors.text.hex};
    display: flex !important;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.3rem;
  }

  & .ant-upload.ant-upload-select .ant-upload > div,
  & .ant-upload-select.ant-upload-select-picture-card .ant-upload > div {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
  }

  & .ant-upload.ant-upload-select .ant-upload > div > div,
  & .ant-upload-select.ant-upload-select-picture-card .ant-upload > div > div {
    margin-top: 0 !important;
    font-size: 1.15rem;
    font-weight: 500;
    line-height: 1.2;
    color: ${colors.text.hex};
  }

  & .ant-upload.ant-upload-select .anticon,
  & .ant-upload-select.ant-upload-select-picture-card .anticon {
    color: ${colors.primary.hex};
    font-size: 1.7rem;
  }
`;

const StatementBox = styled.div`
  width: 100%;

  & .ant-input {
    border-radius: 0.6rem !important;
    background-color: ${card.innerBg} !important;
    border-color: ${card.border} !important;
    color: ${colors.text.hex} !important;
    min-height: 6rem;
  }

  & .ant-input::placeholder {
    color: ${colors.text.hex} !important;
    opacity: 0.4 !important;
  }

  & .ant-input:hover {
    border-color: ${colors.primary.hex} !important;
  }

  & .ant-input:focus,
  & .ant-input-focused {
    border-color: ${colors.primary.hex} !important;
    box-shadow: 0 0 0 2px ${card.accentSoft} !important;
  }
`;

const Helper = styled.span`
  font-size: 1.15rem;
  color: ${colors.text.hex};
  opacity: 0.55;
  line-height: 1.4;
`;

const ErrorText = styled.span`
  font-size: 1.15rem;
  color: ${colors.error.hex};
  line-height: 1.4;
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem 1.2rem;
  background: ${card.innerBg};
  border: 1px solid ${card.border};
  border-radius: 0.8rem;

  & .ant-checkbox-wrapper {
    font-size: 1.3rem;
    color: ${colors.text.hex};
  }

  & .ant-checkbox-inner {
    border-color: ${card.border};
  }

  & .ant-checkbox-checked .ant-checkbox-inner {
    background-color: ${colors.primary.hex};
    border-color: ${colors.primary.hex};
  }
`;

const StatementGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.6rem;
  width: 100%;
  container-type: inline-size;

  @container (max-width: 48rem) {
    grid-template-columns: 1fr;
  }
`;
